import {
  Document,
  Font,
  Image,
  Page,
  PDFViewer,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import JsBarcode from "jsbarcode";
import { useMemo } from "react";

import { getFullDate, getMonthYearDate } from "@/utils/dateFormater";
import moneyFormatter from "@/utils/moneyFormat";

// Using URL constructor for Vite's asset handling
const interUrl = new URL("../../../../../public/Inter.ttf", import.meta.url).href;
const interSemiUrl = new URL("../../../../../public/InterSemi.ttf", import.meta.url).href;
const graphUrl = new URL("../../../../../public/Graph.png", import.meta.url).href;

const BillPdf = ({ bill, paymentInfo }) => {
  Font.register({
    family: "Inter",
    fonts: [
      {
        src: interUrl,
      },
      {
        fontWeight: "600",
        src: interSemiUrl,
      },
    ],
  } as any);

  const styles = StyleSheet.create({
    page: {
      flexDirection: "row",
      backgroundColor: "#E4E4E4",
      fontFamily: "Inter",
    },
    header: {
      backgroundColor: "#183F38",
      paddingHorizontal: 48,
      paddingVertical: 32,
      flexDirection: "row",
      justifyContent: "space-between",
      gap: 40,
      color: "#fff",
      position: "relative",
      w: "100%",
    },
    graph: {
      display: "flex",
      position: "absolute",
      height: 110,
      width: 600,
      bottom: 0,
      right: 0,
    },
    basicContainer: {
      display: "flex",
      flexDirection: "column",
    },
    logo: {
      position: "absolute",
      top: 32,
      right: 56,
      width: 130,
    },
    title: {
      fontWeight: 600,
      fontSize: 32,
      fontFamily: "Inter",
    },
    topSection: {
      paddingHorizontal: 48,
      paddingVertical: 20,
      gap: 40,
      flexDirection: "row",
    },
    topSectionPart: {
      gap: 16,
      flex: 1,
      flexDirection: "column",
    },
    topSectionInnerPart: {
      gap: 8,
    },
    lightText: {
      color: "#425466",
      fontSize: 12,
      fontFamily: "Inter",
    },
    darkText: {
      color: "#1D1F31",
      fontSize: 12,
      fontFamily: "Inter",
    },
    subHeader: {
      backgroundColor: "#EFF0F0",
      flexDirection: "row",
      gap: 40,
      paddingHorizontal: 48,
      paddingVertical: 6,
    },
    lowerContent: {
      paddingVertical: 10,
    },
    line: {
      paddingHorizontal: 48,
      paddingVertical: 4,
      flexDirection: "row",
      gap: 40,
    },
    lineContent: {
      flex: 1,
      flexDirection: "row",
      justifyContent: "space-between",
    },
    divider: {
      height: 1,
      backgroundColor: "#DADADA",
    },
    total: {
      color: "#191B2C",
      fontWeight: 600,
      fontSize: 12,
      fontFamily: "Inter",
    },
    footer: {
      paddingHorizontal: 48,
      paddingVertical: 16,
      flexDirection: "row",
      justifyContent: "space-between",
      gap: 16,
      backgroundColor: "#EFF0F0",
    },
    verticalDivider: {
      height: "100%",
      width: 1,
      backgroundColor: "#DADADA",
    },
    barCodeSection: {
      gap: 8,
    },
  });

  const referenceMonth = useMemo(() => {
    return getMonthYearDate(new Date(bill.reference_month));
  }, [bill.reference_month]);

  const dueDate = useMemo(() => {
    return getFullDate(new Date(bill.due_date));
  }, [bill.due_date]);

  const subTotal = useMemo(() => bill.price - bill.geedee_fee + bill.discount, [bill]);
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, paymentInfo.boleto_code, { displayValue: false });
  const barcode = canvas.toDataURL();

  return (
    <PDFViewer className="h-screen w-screen">
      <Document>
        <Page size="A4">
          <View style={styles.header}>
            <Image src={graphUrl} style={styles.graph} />
            <View style={[styles.basicContainer, { gap: 32 }]}>
              <Text style={[styles.title, { fontFamily: "Inter" }]}>FATURA</Text>

              <View style={[styles.basicContainer, { gap: 24 }]}>
                <View style={[styles.basicContainer, { gap: 16 }]}>
                  <Text style={{ opacity: 0.75, fontFamily: "Inter", fontSize: 14 }}>
                    Número da fatura
                  </Text>
                  <Text style={{ fontFamily: "Inter", fontSize: 14 }}>{bill.id}</Text>
                </View>

                <View style={[styles.basicContainer, { gap: 16 }]}>
                  <Text style={{ opacity: 0.75, fontFamily: "Inter", fontSize: 14 }}>
                    Mês de referência
                  </Text>
                  <Text style={{ fontFamily: "Inter", fontSize: 14 }}>{referenceMonth}</Text>
                </View>
              </View>
            </View>
            <View style={styles.logo}>
              <Text style={[styles.title, { fontFamily: "Inter" }]}>geedee</Text>
            </View>
            <View />
          </View>

          <View style={styles.topSection}>
            <View style={styles.topSectionPart}>
              <View style={{ gap: 12 }}>
                <Text style={styles.lightText}>Para:</Text>

                <View style={styles.topSectionInnerPart}>
                  <Text style={styles.darkText}>{bill.consumer.name}</Text>

                  <Text style={styles.darkText}>
                    {bill.consumer.facilities?.[0]?.address.address}
                  </Text>

                  <Text style={styles.darkText}>{bill.consumer.email}</Text>
                </View>
              </View>

              <View style={styles.topSectionInnerPart}>
                <Text style={styles.lightText}>Tarifa Compensável</Text>
                <Text style={[styles.darkText, { fontSize: 14 }]}>
                  {moneyFormatter(bill.compensable_tariff_cents)}
                </Text>
              </View>
            </View>
            <View style={styles.topSectionPart}>
              <View style={{ gap: 12, flex: 1 }}>
                <Text style={styles.lightText}>De:</Text>

                <View style={styles.topSectionInnerPart}>
                  <Text style={styles.darkText}>Geedee</Text>

                  <Text style={styles.darkText}>geedee.com.br</Text>
                </View>
              </View>

              <View style={styles.topSectionInnerPart}>
                <Text style={styles.lightText}>Data de vencimento</Text>
                <Text style={[styles.darkText, { fontSize: 14 }]}>{dueDate}</Text>
              </View>
            </View>
          </View>

          <View style={styles.subHeader}>
            <Text style={[styles.darkText, { flex: 1, fontSize: 10 }]}>Item</Text>

            <View style={styles.lineContent}>
              <Text style={[styles.darkText, { fontSize: 10 }]}>Quantidade</Text>
              <Text style={[styles.darkText, { fontSize: 10 }]}>Preço</Text>
            </View>
          </View>

          <View style={styles.lowerContent}>
            <View style={styles.line}>
              <Text style={[styles.lineContent, styles.darkText]}>Energia Injetada</Text>

              <View style={styles.lineContent}>
                <Text style={styles.darkText}>{bill.compensated_kwh}</Text>
                <Text style={styles.darkText}>{moneyFormatter(bill.price_without_geedee)}</Text>
              </View>
            </View>

            <View style={styles.line}>
              <View style={styles.lineContent} />

              <View style={[styles.lineContent, styles.divider]} />
            </View>

            <View style={styles.line}>
              <View style={styles.lineContent} />

              <View style={styles.lineContent}>
                <Text style={styles.lightText}>Subtotal</Text>
                <Text style={styles.lightText}>{moneyFormatter(subTotal)}</Text>
              </View>
            </View>

            <View style={styles.line}>
              <View style={styles.lineContent} />

              <View style={styles.lineContent}>
                <Text style={styles.lightText}>
                  Desconto ({(bill.discount_percentage * 100).toFixed(2)}%)
                </Text>
                <Text style={styles.lightText}>{moneyFormatter(bill.discount)}</Text>
              </View>
            </View>

            <View style={styles.line}>
              <View style={styles.lineContent} />

              <View style={[styles.lineContent, styles.divider]} />
            </View>

            <View style={styles.line}>
              <View style={styles.lineContent} />

              <View style={styles.lineContent}>
                <Text style={styles.total}>Valor Total</Text>
                <Text style={styles.total}>{moneyFormatter(bill.price)}</Text>
              </View>
            </View>
          </View>

          <View
            style={{
              flex: 1,
              paddingHorizontal: 40,
              gap: 20,
              justifyContent: "space-between",
              flexDirection: "row",
            }}
          >
            <View style={{ gap: 12, flex: 1 }}>
              {paymentInfo.type === "Consortium" ? (
                <Image src={barcode} style={{ width: "100%", height: 75 }} />
              ) : (
                <View style={{ height: 72 }} />
              )}

              <View style={{ gap: 10, flexDirection: "row", justifyContent: "center" }}>
                {paymentInfo.type === "TED" && (
                  <>
                    <View style={styles.barCodeSection}>
                      <Text style={styles.darkText}>
                        {paymentInfo?.payment_info?.pix_key ?? "Não informado"}
                      </Text>
                      <Text style={[styles.lightText, { fontSize: 10 }]}>Chave Pix</Text>
                    </View>

                    <View style={styles.verticalDivider} />
                  </>
                )}

                <View style={styles.barCodeSection}>
                  <Text style={styles.darkText}>
                    {getMonthYearDate(new Date(bill.reference_month))}
                  </Text>
                  <Text style={[styles.lightText, { fontSize: 10 }]}>Mês de referencia</Text>
                </View>

                <View style={styles.verticalDivider} />

                <View style={styles.barCodeSection}>
                  <Text style={styles.darkText}>{getFullDate(new Date(bill.due_date))}</Text>
                  <Text style={[styles.lightText, { fontSize: 10 }]}>Data de vencimento</Text>
                </View>

                <View style={styles.verticalDivider} />

                <View style={styles.barCodeSection}>
                  <Text style={styles.darkText}>{moneyFormatter(bill.price)}</Text>
                  <Text style={[styles.lightText, { fontSize: 10 }]}>Valor total</Text>
                </View>
              </View>
            </View>
          </View>

          <View style={styles.footer}>
            <Text style={[styles.darkText, { flex: 1 }]}>geedee.com.br</Text>
          </View>
        </Page>
      </Document>
    </PDFViewer>
  );
};

export default BillPdf;
